import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import Spinner from '../Spinner';
import './Register.css';

interface RegisterProps {
  onRegisterSuccess?: (token: string) => void;
  onBackToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onBackToLogin }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    document.title = 'AVM Analytics - Регистрация';
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await apiClient.register(firstName, lastName, username, password, secretKey);
      setIsLoading(false);
      onBackToLogin();
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Произошла неизвестная ошибка. Попробуйте снова.');
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page-register">
      <div className="auth-shell">
        <section className="auth-panel auth-panel-single" aria-label="Форма регистрации">
          <div className="auth-panel-header auth-panel-header-centered">
            <span className="auth-panel-kicker">AVM Analytics</span>
            <div className="auth-brand">
              <h1 className="auth-brand-title">AVM Analytics</h1>
              <p className="auth-brand-subtitle">
                Создайте учетную запись для доступа к рабочему пространству.
              </p>
            </div>

            <div className="auth-toggle" role="tablist" aria-label="Переключение формы доступа">
              <button
                className="auth-toggle-btn"
                type="button"
                role="tab"
                aria-selected="false"
                onClick={() => onBackToLogin()}
              >
                Вход
              </button>
              <button
                className="auth-toggle-btn active"
                type="button"
                role="tab"
                aria-selected="true"
              >
                Регистрация
              </button>
            </div>
          </div>

          <div className="auth-card auth-card-register">
            <div className="auth-card-head">
              <h2 className="auth-card-title">Регистрация</h2>
              <p className="auth-card-description">
                Заполните основные данные и подтвердите создание аккаунта внутренним ключом.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group form-group-row">
                <div>
                  <label className="form-label" htmlFor="firstName">
                    Имя
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="form-input"
                      placeholder="Иван"
                      required
                      autoComplete="given-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label" htmlFor="lastName">
                    Фамилия
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="form-input"
                      placeholder="Иванов"
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="username">
                  Имя пользователя
                </label>
                <div className="input-wrapper">
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="form-input"
                    placeholder="Придумайте логин"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="form-label-row">
                  <label className="form-label" htmlFor="password">
                    Пароль
                  </label>
                  <button
                    type="button"
                    className="password-hint"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  </button>
                </div>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Придумайте пароль"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="form-label-row">
                  <label className="form-label" htmlFor="secretKey">
                    Secret key
                  </label>
                  <button
                    type="button"
                    className="password-hint"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    aria-label={showSecretKey ? 'Скрыть ключ' : 'Показать ключ'}
                  >
                    {showSecretKey ? 'Скрыть ключ' : 'Показать ключ'}
                  </button>
                </div>
                <div className="input-wrapper">
                  <input
                    id="secretKey"
                    type={showSecretKey ? 'text' : 'password'}
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    className="form-input form-input-accent"
                    placeholder="Введите внутренний ключ доступа"
                    required
                    autoComplete="off"
                  />
                </div>
              </div>

              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}

              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? <Spinner mini /> : 'Создать аккаунт'}
              </button>
            </form>

            <div className="toggle-text">
              <span>Уже есть аккаунт?</span>
              <button
                type="button"
                className="toggle-link"
                onClick={() => onBackToLogin()}
              >
                Вернуться ко входу
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Register;
